import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { formatRut } from 'rutlib/lib';
import { environment } from 'src/environments/environment';
import { EntregaEquipoLink } from 'src/app/shared/models/entrega-equipo-link.interface';

@Injectable({
  providedIn: 'root'
})
export class EntregaEquipoPdfService {

  pdfMake: any;

  constructor(private http: HttpClient) { }

  private async loadPdfMaker() {
    if (!this.pdfMake) {
      const pdfMakeModule = await import('pdfmake/build/pdfmake');
      const pdfFontsModule = await import('pdfmake/build/vfs_fonts');
      this.pdfMake = pdfMakeModule.default;
      this.pdfMake.vfs = pdfFontsModule.default.pdfMake.vfs;
    }
  }

  // Convierte la imagen de firma/hoja física (servida como archivo estático) a base64
  // para poder embeberla directamente en el PDF generado en el navegador.
  private async obtenerImagenBase64(path: string): Promise<string | null> {
    try {
      const blob = await this.http.get(`${environment.API}${path}`, { responseType: 'blob' }).toPromise();
      return await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = () => resolve(null);
        reader.readAsDataURL(blob as Blob);
      });
    } catch {
      return null;
    }
  }

  async generarPdf(link: EntregaEquipoLink) {
    await this.loadPdfMaker();

    const rutFormateado = link.esExtranjero ? link.rut : formatRut(link.rut);
    const equipo = (link as any).EquipoTecnologico;
    const trabajadorNombre = link.nombreTrabajador || (link as any).Trabajador
      ? `${(link as any).Trabajador?.nombres || ''} ${(link as any).Trabajador?.apellidos || ''}`.trim() || link.nombreTrabajador
      : link.nombreTrabajador;

    const esImagen = (path: string | null | undefined) => !!path && /\.(jpe?g|png|gif|bmp)$/i.test(path);

    const imagenFirma = (link.firmaPath && esImagen(link.firmaPath)) ? await this.obtenerImagenBase64(link.firmaPath) : null;
    const imagenHoja = (!imagenFirma && link.hojaFisicaPath && esImagen(link.hojaFisicaPath)) ? await this.obtenerImagenBase64(link.hojaFisicaPath) : null;
    const hayAdjuntoNoImagen = !imagenFirma && !imagenHoja && (link.firmaPath || link.hojaFisicaPath);

    const fechaFirma = link.fechaFirma ? new Date(link.fechaFirma) : new Date();

    const filasDatos = [
      ['Rut', rutFormateado],
      ['Nombre', trabajadorNombre || 'Sin vincular'],
      ['Tipo de movimiento', link.tipoMovimiento],
      ['Fecha', fechaFirma.toLocaleString('es-CL')]
    ];

    const filasEquipo: string[][] = [];
    if (equipo) {
      filasEquipo.push(['Tipo', equipo.tipo]);
      filasEquipo.push(['Marca', equipo.marca]);
      filasEquipo.push(['Modelo', equipo.modelo]);
      if (equipo.chip) filasEquipo.push(['Chip', equipo.chip]);
      filasEquipo.push(['N° de serie', equipo.numeroSerie]);
      if (equipo.accesorios) filasEquipo.push(['Accesorios', equipo.accesorios]);
    } else {
      filasEquipo.push(['Equipo', 'Sin datos de equipo']);
    }

    const tablaDatos = (filas: string[][]) => ({
      table: {
        widths: [130, '*'],
        body: filas.map(([label, valor]) => [
          { text: label, bold: true, fillColor: '#f2f2f2', fontSize: 10 },
          { text: valor, fontSize: 10 }
        ])
      },
      layout: 'lightHorizontalLines',
      margin: [0, 3, 0, 8]
    });

    const content: any[] = [
      { text: 'ANEXO DE ENTREGA DE EQUIPO TECNOLÓGICO', style: 'header' },

      { text: 'Datos generales', style: 'subheader', margin: [0, 8, 0, 0] },
      tablaDatos(filasDatos),

      { text: 'Equipo', style: 'subheader', margin: [0, 0, 0, 0] },
      tablaDatos(filasEquipo),

      link.tipoMovimiento === 'DEVOLUCION'
        ? {
          text: `En Santiago, ${fechaFirma.getDate()} de ${fechaFirma.getMonth() + 1} del ${fechaFirma.getFullYear()}, por intermedio del presente documento, yo ${(trabajadorNombre || 'SIN VINCULAR').toUpperCase()}, cédula nacional de identidad número ${rutFormateado}, declaro que, con esta fecha, devuelvo a la empresa el equipo detallado anteriormente, quedando la empresa en poder del mismo.`,
          style: 'otro'
        }
        : {
          text: `En Santiago, ${fechaFirma.getDate()} de ${fechaFirma.getMonth() + 1} del ${fechaFirma.getFullYear()}, por intermedio del presente documento, yo ${(trabajadorNombre || 'SIN VINCULAR').toUpperCase()}, cédula nacional de identidad número ${rutFormateado}, declaro que, con esta fecha, recibí de la empresa el equipo tecnológico detallado anteriormente.`,
          style: 'otro'
        },
    ];

    if (link.tipoMovimiento !== 'DEVOLUCION') {
      content.push(
        { text: 'La dotación que por este acto se entrega para estar a cargo del trabajador, es de propiedad de la empresa en todo momento, por lo que, en caso de término del contrato de trabajo o de la entrega de una nueva dotación, el trabajador se compromete a realizar la devolución de forma inmediata.', style: 'otro', margin: [0, 6, 0, 0] },
        { text: 'En caso de que el equipo sufra daño en forma total o parcial, el trabajador deberá informar a la brevedad al empleador, para su reparación y/o reemplazo.', style: 'otro', margin: [0, 6, 0, 0] },
        { text: 'El trabajador reconoce que el bien que por este acto se le entrega es de propiedad y dominio absoluto de su empleador.', style: 'otro', margin: [0, 6, 0, 0] },
        { text: 'El trabajador, por medio de este acto, se compromete a mantener un correcto y adecuado cuidado del equipo que por este acto se le entrega.', style: 'otro', margin: [0, 6, 0, 0] },
        { text: 'POR ESTE ACTO, EL TRABAJADOR AUTORIZA EXPRESAMENTE AL EMPLEADOR PARA REALIZAR LAS DEDUCCIONES O DESCUENTOS EN DINERO, YA SEA DE LA REMUNERACIÓN Y/O FINIQUITO, POR EL DAÑO TOTAL O PARCIAL, POR PÉRDIDA O EXTRAVÍO DEL EQUIPO ENTREGADO, CAUSADAS POR SU RESPONSABILIDAD, O LA NO ENTREGA DEL EQUIPO EN TIEMPO INMEDIATO DE TERMINAR LA RELACIÓN LABORAL.', style: 'subheaderr', margin: [0, 8, 0, 0] }
      );
    }

    if (link.observacion) {
      content.push({ text: 'Observación', style: 'subheader', margin: [0, 8, 0, 0] });
      content.push({ text: link.observacion, fontSize: 10 });
    }

    if (imagenFirma) {
      content.push({
        unbreakable: true,
        stack: [
          { text: 'Firma', style: 'subheader', margin: [0, 30, 0, 0] },
          { image: imagenFirma, width: 200, margin: [0, 10, 0, 0] },
          {
            canvas: [{ type: 'line', x1: 0, y1: 0, x2: 180, y2: 0, lineWidth: 1, lineColor: '#000000' }],
            margin: [0, 2, 0, 2]
          },
          { text: (trabajadorNombre || 'Sin vincular').toUpperCase(), bold: true, fontSize: 10 },
          { text: `RUT: ${rutFormateado}`, fontSize: 9 }
        ]
      });
    } else if (imagenHoja) {
      content.push({ text: '\nDocumento firmado en papel (respaldo adjunto)', style: 'subheader' });
      content.push({ image: imagenHoja, width: 400 });
    } else if (hayAdjuntoNoImagen) {
      content.push({ text: `\nHay un documento adjunto (${link.firmaPath || link.hojaFisicaPath}) que no se pudo incrustar automáticamente en este PDF (no es una imagen). Descárguelo directamente desde el servidor si necesita verlo.`, italics: true });
    } else {
      content.push({ text: '\n(Sin firma ni respaldo adjunto disponible para incluir en este PDF)', italics: true });
    }

    const dd = {
      pageMargins: [40, 30, 40, 30],
      content,
      styles: {
        header: { fontSize: 13, bold: true },
        subheader: { fontSize: 11, bold: true },
        subheaderr: { fontSize: 9.5, bold: true, lineHeight: 1.25, alignment: 'justify' },
        otro: { fontSize: 10, alignment: 'justify', lineHeight: 1.25 }
      }
    };

    this.pdfMake.createPdf(dd).open();
  }

}
